import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import { computePosition, flip, shift, offset } from "@floating-ui/dom";

export default class extends Controller {
  static values = {
    dropdownInput: { type: Boolean, default: true },
    dropdownInputPlaceholder: { type: String, default: "Search..." },
    clearButton: { type: Boolean, default: false },
    disableTyping: { type: Boolean, default: false },
    allowNew: { type: Boolean, default: false },
    optgroupColumns: { type: Boolean, default: false },
    submitOnChange: { type: Boolean, default: false },
  };

  connect() {
    if (this.element.tomselect) return;

    const options = this.#buildOptions();
    this.select = new TomSelect(this.element, options);

    this.#setupEventHandlers();
    this.#setupPositioning();

    this.element.style.visibility = "visible";
  }

  disconnect() {
    this.#cleanup();
  }

  // Private methods

  #buildOptions() {
    const plugins = this.#getPlugins();
    const baseOptions = {
      plugins,
      maxOptions: null,
      closeAfterSelect: !this.element.multiple,
      create: this.allowNewValue,
      render: this.#getRenderConfig(),
      onChange: this.#handleChange.bind(this),
      onDropdownOpen: () => {
        this.#updatePosition();
        this.#preventSearchAutoFocus();
      },
    };

    return baseOptions;
  }

  #getPlugins() {
    const plugins = [];
    const isMultiple = this.element.multiple;

    if (isMultiple) {
      plugins.push("remove_button", "checkbox_options", "drag_drop", "no_active_items");
    }

    if (this.optgroupColumnsValue) plugins.push("optgroup_columns");
    if (this.dropdownInputValue) plugins.push("dropdown_input");
    if (this.clearButtonValue && !isMultiple) plugins.push("clear_button");

    return plugins;
  }

  #getRenderConfig() {
    return {
      option: (data, escape) => this.#renderStandardOption(data, escape),
      item: (data, escape) => this.#renderStandardItem(data, escape),
      option_create: (data, escape) => 
        `<div class="create">Add <strong>${escape(data.input)}</strong>&hellip;</div>`,
    };
  }

  #renderStandardOption(data, escape) {
    return `
      <div class="option">
        <span>${escape(data.text)}</span>
      </div>
    `;
  }

  #renderStandardItem(data, escape) {
    return `<div>${escape(data.text)}</div>`;
  }

  #handleChange(value) {
    if (this.submitOnChangeValue && this.element.form) {
      this.element.form.requestSubmit();
    }
  }

  #setupEventHandlers() {
    if (!this.select) return;

    // Handle form reset
    if (this.element.form) {
      this.element.form.addEventListener("reset", () => {
        setTimeout(() => this.select?.clear(), 0);
      });
    }

    // Disable typing if requested
    if (this.disableTypingValue) {
      this.select.control_input.readOnly = true;
    }

    // Custom placeholder for dropdown input
    if (this.dropdownInputValue && this.dropdownInputPlaceholderValue) {
      const dropdownInput = this.select.dropdown_content?.querySelector(".dropdown-input");
      if (dropdownInput) {
        dropdownInput.placeholder = this.dropdownInputPlaceholderValue;
      }
    }
  }

  #setupPositioning() {
    if (!this.select?.dropdown) return;

    const updatePosition = () => {
      if (!this.select?.isOpen) return;

      computePosition(this.select.control, this.select.dropdown, {
        placement: "bottom-start",
        middleware: [
          offset(4),
          flip({
            fallbackPlacements: ["top-start", "bottom-start"],
          }),
          shift({ padding: 8 }),
        ],
      }).then(({ x, y }) => {
        if (!this.select?.dropdown) return;
        Object.assign(this.select.dropdown.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    };

    this.updatePosition = updatePosition;

    // Update position on scroll and resize
    ["scroll", "resize"].forEach((event) => {
      window.addEventListener(event, updatePosition, { passive: true });
    });
  }

  #updatePosition() {
    if (this.updatePosition) {
      this.updatePosition();
    }
  }

  #preventSearchAutoFocus() {
    // Prevent the dropdown search input from auto-focusing and showing keyboard
    if (this.dropdownInputValue) {
      const dropdownInput = this.select?.dropdown_content?.querySelector(".dropdown-input");
      if (dropdownInput) {
        // Set readonly to prevent keyboard popup
        dropdownInput.readOnly = true;
        
        // Remove readonly when user explicitly clicks to search
        dropdownInput.addEventListener('click', () => {
          dropdownInput.readOnly = false;
          dropdownInput.focus();
        }, { once: true });
      }
    }
  }

  #cleanup() {
    if (this.select) {
      this.select.destroy();
      this.select = null;
    }

    if (this.updatePosition) {
      ["scroll", "resize"].forEach((event) => {
        window.removeEventListener(event, this.updatePosition);
      });
    }
  }
}

